import { LinearProgress } from "@mui/material";
import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  PET_INFINITE_APPOINTMENTS,
  USER_INFINITE_APPOINTMENTS,
} from "../../../../enums/ApiPaths";
import { STATUSES } from "../../../../enums/AppointmentStatus";
import { APPOINTMENTS_INFINITE } from "../../../../enums/QueryKeys";
import { useGetInfiniteAppointments } from "../../../../hooks/useGetAppointments";
import { futurePastFilterKeys } from "../../enums/UserAppointmentFuturePastFilterKeys";
import { AppointmentMapperInfinite } from "./components/AppointmentMapper";
import AppointmentUserFilters from "./components/AppointmentUserFilters";

const timeFrame = "past";

function useActiveFilters(searchParams) {
  return useMemo(() => {
    const filterKeys = ["service", "pet", "from", "to", "status"];
    return filterKeys.some((key) => searchParams.get(key));
  }, [searchParams]);
}

export default function Past({ isOwnerDeleted = false }) {
  const [searchParams] = useSearchParams();
  const hasActiveFilters = useActiveFilters(searchParams);

  const filterParams = futurePastFilterKeys.reduce((acc, key) => {
    const value = searchParams.get(key);
    if (value && value !== "null") {
      acc[key] = value;
    }
    return acc;
  }, {});

  const queryKey = [
    APPOINTMENTS_INFINITE,
    timeFrame,
    USER_INFINITE_APPOINTMENTS,
    filterParams,
  ];
  const {
    data,
    isLoading,
    fetchNextPage,
    isFetchNextPageError,
    isFetchingNextPage,
    hasNextPage,
  } = useGetInfiniteAppointments(
    timeFrame,
    USER_INFINITE_APPOINTMENTS,
    queryKey,
    filterParams,
  );

  const pastStatusTitle = [
    { title: STATUSES.FINISHED, value: STATUSES.FINISHED },
    { title: STATUSES.RESOLVED, value: STATUSES.RESOLVED },
    { title: STATUSES.INACTIVE, value: STATUSES.INACTIVE },
  ];

  return (
    <>
      <AppointmentUserFilters
        statusOptions={pastStatusTitle}
        timeframe={timeFrame}
      />

      <ShowData
        isOwnerDeleted={isOwnerDeleted}
        data={data}
        isLoading={isLoading}
        fetchNextPage={fetchNextPage}
        isFetchNextPageError={isFetchNextPageError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        hasActiveFilters={hasActiveFilters}
      />
    </>
  );
}

export function PastPet({ isOwnerDeleted = false }) {
  const { id: petId } = useParams();
  const queryKey = [
    APPOINTMENTS_INFINITE,
    timeFrame,
    PET_INFINITE_APPOINTMENTS + "/" + petId,
  ];
  const {
    data,
    isLoading,
    fetchNextPage,
    isFetchNextPageError,
    isFetchingNextPage,
    hasNextPage,
  } = useGetInfiniteAppointments(
    timeFrame,
    PET_INFINITE_APPOINTMENTS + "/" + petId,
    queryKey,
  );

  return (
    <ShowData
      isOwnerDeleted={isOwnerDeleted}
      data={data}
      isLoading={isLoading}
      fetchNextPage={fetchNextPage}
      isFetchNextPageError={isFetchNextPageError}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={hasNextPage}
    />
  );
}

function ShowData({
  data,
  isLoading,
  fetchNextPage,
  isFetchNextPageError,
  isFetchingNextPage,
  hasNextPage,
  hasActiveFilters = false,
  isOwnerDeleted = false,
}) {
  if (isLoading) {
    return <LinearProgress />;
  }

  const pages = data?.pages ?? [];
  const content = pages?.flatMap((page) => page?.content) ?? [];
  const title = hasActiveFilters
    ? "NoPastAppointmentsWithFilters"
    : "NoPastAppointments";
  return (
    <AppointmentMapperInfinite
      hasNextPage={hasNextPage}
      isFetchNextPageError={isFetchNextPageError}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
      appointments={content}
      title={title}
      isOwnerDeleted={isOwnerDeleted}
      containerHeight="66vh"
    />
  );
}
